import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { StructuredModal } from '../../../shared/components/modals';
import { Button } from '../../../shared/components/buttons';
import {
  JobClosingSurveyProps,
  JobClosingSurveyData,
  JobClosingSurveyState,
  ValidationErrors,
  OUTCOME_OPTIONS,
  OutcomeType,
  getNPSCategory
} from '../types/JobClosingSurvey';
import { validateJobClosingSurvey, validateField } from '../utils/jobClosingSurveyValidation';
import { CloseJobError, extractCloseJobError } from '../utils/closeJobError';

const JobClosingSurveyModal: React.FC<JobClosingSurveyProps> = ({
  job,
  isVisible,
  onClose,
  onSuccess,
  onError
}) => {
  const [state, setState] = useState<JobClosingSurveyState>({
    formData: {
      job_id: job.id,
      outcome: '' as OutcomeType,
      outcome_other: '',
      additional_comments: '',
      recommendation_score: undefined
    },
    validationErrors: {},
    isSubmitting: false,
    showOtherInput: false,
    touched: {
      outcome: false,
      outcome_other: false,
      additional_comments: false,
      recommendation_score: false
    }
  });
  
  const [skipFutureSurveys, setSkipFutureSurveys] = useState(false);

  // A refused close (403, 422, 500) is shown here, inside the dialog, so the
  // member can read the reason and retry or cancel without the backdrop hiding
  // a flash alert behind the modal.
  const [submitError, setSubmitError] = useState<CloseJobError | null>(null);

  // Reset form when modal opens/closes or job changes
  useEffect(() => {
    if (isVisible) {
      resetForm();
    }
  }, [isVisible, job.id]);

  const resetForm = useCallback(() => {
    setSubmitError(null);
    setState({
      formData: {
        job_id: job.id,
        outcome: '' as OutcomeType,
        outcome_other: '',
        additional_comments: '',
        recommendation_score: undefined
      },
      validationErrors: {},
      isSubmitting: false,
      showOtherInput: false,
      touched: {
        outcome: false,
        outcome_other: false,
        additional_comments: false,
        recommendation_score: false
      }
    });
  }, [job.id]);

  const updateFormData = useCallback((updates: Partial<JobClosingSurveyData>) => {
    // Any answer change means the member is acting on the refusal, so drop it.
    setSubmitError(null);
    setState(prevState => ({
      ...prevState,
      formData: { ...prevState.formData, ...updates }
    }));
  }, []);

  const markFieldTouched = useCallback((fieldName: keyof JobClosingSurveyState['touched']) => {
    setState(prevState => ({
      ...prevState,
      touched: { ...prevState.touched, [fieldName]: true }
    }));
  }, []);

  const setValidationError = useCallback((fieldName: keyof ValidationErrors, error: string | null) => {
    setState(prevState => ({
      ...prevState,
      validationErrors: {
        ...prevState.validationErrors,
        [fieldName]: error
      }
    }));
  }, []);

  const handleOutcomeChange = useCallback((outcome: OutcomeType) => {
    updateFormData({ outcome, outcome_other: '' });
    setState(prevState => ({
      ...prevState,
      showOtherInput: outcome === 'other'
    }));
    markFieldTouched('outcome');

    // Clear validation errors when user makes a selection
    if (state.validationErrors.outcome) {
      setValidationError('outcome', null);
    }
  }, [updateFormData, markFieldTouched, setValidationError, state.validationErrors.outcome]);

  const handleOtherInputChange = useCallback((value: string) => {
    updateFormData({ outcome_other: value });
    markFieldTouched('outcome_other');

    // Validate field in real-time if it's been touched
    if (state.touched.outcome_other || value.length > 0) {
      const error = validateField('outcome_other', value, { ...state.formData, outcome_other: value });
      setValidationError('outcome_other', error);
    }
  }, [updateFormData, markFieldTouched, setValidationError, state.formData, state.touched.outcome_other]);

  const handleNPSScoreChange = useCallback((score: number) => {
    updateFormData({ recommendation_score: score });
    markFieldTouched('recommendation_score');
  }, [updateFormData, markFieldTouched]);

  const handleAdditionalCommentsChange = useCallback((value: string) => {
    updateFormData({ additional_comments: value });
    markFieldTouched('additional_comments');

    // Validate length in real-time
    if (value.length > 1000) {
      setValidationError('general', 'Additional comments must be under 1000 characters');
    } else if (state.validationErrors.general?.includes('Additional comments')) {
      setValidationError('general', null);
    }
  }, [updateFormData, markFieldTouched, setValidationError, state.validationErrors.general]);

  const getCSRFToken = useCallback((): string => {
    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.getAttribute('content');
    if (!token) {
      throw new Error('CSRF token not found');
    }
    return token;
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission
    if (state.isSubmitting) {
      return;
    }

    // Immediately set submitting state
    setState(prevState => ({ ...prevState, isSubmitting: true }));

    // Mark all fields as touched for validation display
    setState(prevState => ({
      ...prevState,
      touched: {
        outcome: true,
        outcome_other: true,
        additional_comments: true,
        recommendation_score: true
      },
      isSubmitting: true
    }));

    const validation = validateJobClosingSurvey(state.formData);

    if (!validation.isValid) {
      setState(prevState => ({
        ...prevState,
        validationErrors: validation.errors,
        isSubmitting: false
      }));
      return;
    }

    try {
      const csrfToken = getCSRFToken();

      const response = await axios.post('/job_closing_surveys', {
        ...state.formData,
        skip_future_surveys: skipFutureSurveys
      }, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      });

      console.log('Survey submission response:', response.data);
      
      // Update the DOM element with the new preference if it was changed
      if (response.data.skip_preference_updated) {
        const userDataElement = document.querySelector('[data-user-skip-survey]');
        if (userDataElement) {
          userDataElement.setAttribute('data-user-skip-survey', 'true');
          console.log('Updated user preference in DOM to skip future surveys');
        }
      }

      onSuccess?.('Thank you for your feedback. Your job has been closed.');
      
      // Small delay before reload to show success message
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error submitting job closing survey:', error);
      const closeError = extractCloseJobError(
        error,
        'An error occurred while submitting the survey. Please try again.'
      );

      setSubmitError(closeError);
      onError?.(closeError.message);
      setState(prevState => ({ ...prevState, isSubmitting: false }));
    }
  }, [state.formData, state.isSubmitting, getCSRFToken, onSuccess, onError, skipFutureSurveys]);

  const handleSkipSurvey = useCallback(async () => {
    if (state.isSubmitting) {
      return;
    }
    
    if (!window.confirm('Are you sure you want to close this job without providing feedback?')) {
      return;
    }

    setState(prevState => ({ ...prevState, isSubmitting: true }));

    try {
      const csrfToken = getCSRFToken();

      await axios.post('/job_closing_surveys/skip', { job_id: job.id }, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      });

      onSuccess?.('Your job has been closed.');
      onClose();
    } catch (error) {
      console.error('Error skipping job closing survey:', error);
      const closeError = extractCloseJobError(
        error,
        'An error occurred while closing the job. Please try again.'
      );

      setSubmitError(closeError);
      onError?.(closeError.message);
      setState(prevState => ({ ...prevState, isSubmitting: false }));
    }
  }, [job.id, getCSRFToken, onSuccess, onClose, onError]);

  const renderNPSButtons = () => {
    return (
      <div className="nps-rating-bubbles d-flex flex-wrap justify-content-center align-items-center gap-2 mt-3 mb-3">
        {Array.from({ length: 11 }, (_, i) => i).map(score => {
          const category = getNPSCategory(score);
          const isSelected = state.formData.recommendation_score === score;

          return (
            <div key={score} className="nps-bubble-wrapper">
              <input
                type="radio"
                id={`nps_score_${score}`}
                name="recommendation_score"
                value={score}
                checked={isSelected}
                onChange={() => handleNPSScoreChange(score)}
                className="nps-bubble-input"
              />
              <label
                htmlFor={`nps_score_${score}`}
                className={`nps-bubble-label ${category === 'DETRACTOR' ? 'nps-detractor' :
                    category === 'PASSIVE' ? 'nps-passive' : 'nps-promoter'
                  }`}
              >
                {score}
              </label>
            </div>
          );
        })}
      </div>
    );
  };

  const footerContent = (
    <>
      <Button
        type="button"
        variant="outline-secondary"
        onClick={handleSkipSurvey}
        disabled={state.isSubmitting}
        loading={state.isSubmitting}
      >
        {state.isSubmitting ? 'Closing...' : 'Skip Survey & Close'}
      </Button>
      <Button
        type="submit"
        variant="warning"
        disabled={state.isSubmitting}
        loading={state.isSubmitting}
        form="job-closing-survey-form"
      >
        {state.isSubmitting ? 'Submitting...' : 'Submit & Close Job'}
      </Button>
    </>
  );

  return (
    <StructuredModal
      show={isVisible}
      onHide={onClose}
      title="Job Closing Survey"
      size="lg"
      footer={footerContent}
    >
      <form onSubmit={handleSubmit} id="job-closing-survey-form">
        <p className="mb-3">
          We'd love to know how your hiring went for: <strong>{job.job_title}</strong>
        </p>

        {/* General error message */}
        {state.validationErrors.general && (
          <div className="alert alert-danger mb-3">
            {state.validationErrors.general}
          </div>
        )}

        {/* Outcome Question */}
        <div className="mb-4">
          <h6 className="mb-3">What was the outcome?</h6>

          {state.validationErrors.outcome && state.touched.outcome && (
            <div className="alert alert-danger mb-2">
              {state.validationErrors.outcome}
            </div>
          )}

          {OUTCOME_OPTIONS.map((option) => (
            <div key={option.value} className="form-check mb-2">
              <input
                type="radio"
                id={`outcome_${option.value}`}
                name="outcome"
                value={option.value}
                checked={state.formData.outcome === option.value}
                onChange={() => handleOutcomeChange(option.value)}
                className="form-check-input"
              />
              <label htmlFor={`outcome_${option.value}`} className="form-check-label">
                {option.label}
              </label>
            </div>
          ))}

          {/* Other outcome text area */}
          {state.showOtherInput && (
            <div className="mb-3 mt-3">
              {state.validationErrors.outcome_other && state.touched.outcome_other && (
                <div className="alert alert-danger mb-2">
                  {state.validationErrors.outcome_other}
                </div>
              )}
              <textarea
                id="outcome_other"
                name="outcome_other"
                value={state.formData.outcome_other || ''}
                onChange={(e) => handleOtherInputChange(e.target.value)}
                onBlur={() => markFieldTouched('outcome_other')}
                className="form-control"
                placeholder="Please specify..."
                rows={2}
              />
              <small className="text-muted">
                {(state.formData.outcome_other?.length || 0)}/500 characters
              </small>
            </div>
          )}
        </div>

        {/* NPS Question */}
        <div className="mb-4">
          <h6 className="mb-3">How likely are you to recommend DirectDental to other dental practices?</h6>
          <p className="text-muted small">0 = Not at all likely | 10 = Extremely likely</p>

          {renderNPSButtons()}

          <div className="d-flex justify-content-between mb-4">
            <small className="text-muted">Not at all likely</small>
            <small className="text-muted">Extremely likely</small>
          </div>
        </div>

        {/* Additional Comments */}
        <div className="mb-4">
          <h6 className="mb-2">Any additional comments?</h6>
          <textarea
            id="additional_comments"
            name="additional_comments"
            value={state.formData.additional_comments || ''}
            onChange={(e) => handleAdditionalCommentsChange(e.target.value)}
            onBlur={() => markFieldTouched('additional_comments')}
            className="form-control"
            placeholder="Optional feedback about your experience..."
            rows={3}
          />
          <small className="text-muted">
            {(state.formData.additional_comments?.length || 0)}/1000 characters
          </small>
        </div>

        {/* Do not ask me again checkbox */}
        <div className="mb-3">
          <div className="form-check">
            <input
              type="checkbox"
              id="skip_future_surveys"
              className="form-check-input"
              checked={skipFutureSurveys}
              onChange={(e) => setSkipFutureSurveys(e.target.checked)}
            />
            <label htmlFor="skip_future_surveys" className="form-check-label">
              Do not ask me again (use quick close next time)
            </label>
          </div>
        </div>

        {/* Server refused the close. Stay open so they can retry or cancel. */}
        {submitError && (
          <div className="alert alert-danger mb-0" role="alert" data-testid="close-job-error">
            <div>{submitError.message}</div>
            {submitError.help && (
              <div className="mt-2">{submitError.help}</div>
            )}
          </div>
        )}
      </form>
    </StructuredModal>
  );
};

export default JobClosingSurveyModal;